import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '../../lib/api';
import { adminQueryKeys } from '../../lib/queryKeys';
import type { Team, CreateTeamRequest } from '@sprintpulse/shared';
// import { TIMEZONES } from '@sprintpulse/shared';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Props { team: Team | null; onClose: () => void; onSaved: () => void; }
interface Form {
  name: string; jiraBoardId: string; jiraProjectKey: string; sprintNameFilter: string;
  bitbucketWorkspace: string; bitbucketRepoSlug: string;
  bitbucketPrLabels: string; timezone: string; isActive: boolean; createdBy: string;
  productId: string; valueStream: string;
}

const EMPTY: Form = {
  name: '', jiraBoardId: '', jiraProjectKey: '', sprintNameFilter: '',
  bitbucketWorkspace: '', bitbucketRepoSlug: '', bitbucketPrLabels: '',
  timezone: 'Europe/London', isActive: true, createdBy: '', productId: '', valueStream: '',
};

function teamToForm(t: Team): Form {
  return {
    name: t.name, jiraBoardId: t.jiraBoardId, jiraProjectKey: t.jiraProjectKey,
    bitbucketWorkspace: t.bitbucketWorkspace, bitbucketRepoSlug: t.bitbucketRepoSlug,
    sprintNameFilter: t.sprintNameFilter ?? '',
    bitbucketPrLabels: t.bitbucketPrLabels.join(', '), timezone: t.timezone,
    isActive: t.isActive, createdBy: t.createdBy ?? '',
    productId: t.productId ?? '', valueStream: t.valueStream ?? '',
  };
}

function Field({ label, hint, error, required, children }: {
  label: string; hint?: string; error?: string; required?: boolean; children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>
        {label}{required && <span style={{ color: 'var(--red)' }}> *</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs mt-1 opacity-60" style={{ color: 'var(--text-3)' }}>{hint}</p>}
      {error && (
        <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--red)' }}>
          <AlertCircle size={10} />{error}
        </p>
      )}
    </div>
  );
}

const inputStyle = {
  base: 'w-full px-3 py-2 rounded border text-sm outline-none transition-colors focus:ring-1',
};

export function TeamFormModal({ team, onClose, onSaved }: Props): JSX.Element {
  const isEdit = team !== null;
  const { user } = useAuth();
  const [form, setForm]     = useState<Form>(isEdit ? teamToForm(team) : { ...EMPTY, createdBy: user?.email ?? '' });
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [serverErr, setServerErr] = useState<string | null>(null);

  const { data: products = [] } = useQuery({
    queryKey: adminQueryKeys.products(),
    queryFn: adminApi.listProducts,
  });

  const set = (k: keyof Form) => (v: string | boolean) =>
    setForm((p) => ({ ...p, [k]: v }));

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.jiraBoardId.trim()) e.jiraBoardId = 'Required';
    if (!form.jiraProjectKey.trim()) e.jiraProjectKey = 'Required';
    // if (!form.bitbucketWorkspace.trim()) e.bitbucketWorkspace = 'Required';
    // if (!form.bitbucketRepoSlug.trim()) e.bitbucketRepoSlug = 'Required';
    // if (!form.bitbucketPrLabels.trim()) e.bitbucketPrLabels = 'At least one label required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const extractError = (e: unknown): string => {
    if (e && typeof e === 'object' && 'response' in e) {
      const resp = (e as { response?: { data?: { error?: string }; status?: number } }).response;
      if (resp?.data?.error) return resp.data.error;
      if (resp?.status) return `Server error (${resp.status})`;
    }
    return (e as Error).message ?? 'Unknown error';
  };

  const createMut = useMutation({
    mutationFn: (req: CreateTeamRequest) => adminApi.createTeam(req),
    onSuccess: onSaved,
    onError: (e: unknown) => setServerErr(extractError(e)),
  });
  const updateMut = useMutation({
    mutationFn: (req: { id: string; data: Partial<CreateTeamRequest> }) =>
      adminApi.updateTeam(req.id, req.data),
    onSuccess: onSaved,
    onError: (e: unknown) => setServerErr(extractError(e)),
  });

  const pending = createMut.isPending || updateMut.isPending;

  const handleSubmit = (): void => {
    setServerErr(null);
    if (!validate()) return;
    const payload: CreateTeamRequest = {
      name: form.name.trim(),
      jiraBoardId: form.jiraBoardId.trim(),
      jiraProjectKey: form.jiraProjectKey.toUpperCase().trim(),
      sprintNameFilter: form.sprintNameFilter.trim() || undefined,
      bitbucketWorkspace: form.bitbucketWorkspace.trim(),
      bitbucketRepoSlug: form.bitbucketRepoSlug.trim(),
      bitbucketPrLabels: form.bitbucketPrLabels.split(',').map((l) => l.trim().toLowerCase()).filter(Boolean),
      timezone: form.timezone,
      isActive: form.isActive,
      createdBy: form.createdBy.trim() || undefined,
      productId: form.productId || undefined,
      valueStream: form.valueStream.trim() || undefined,
    };
    if (isEdit) updateMut.mutate({ id: team.id, data: payload });
    else createMut.mutate(payload);
  };

  useEffect(() => {
    const h = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const inputCls = (err?: string): React.CSSProperties => ({
    background: 'var(--bg-subtle)',
    borderColor: err ? 'var(--red)' : 'var(--border)',
    color: 'var(--text-1)',
  });

  const renderInput = (k: keyof Form, opts?: { mono?: boolean; placeholder?: string }) => (
    <input
      type="text"
      value={form[k] as string}
      onChange={(e) => set(k)(e.target.value)}
      placeholder={opts?.placeholder}
      className={`${inputStyle.base} ${opts?.mono ? 'font-mono' : ''}`}
      style={inputCls(errors[k])}
    />
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div
        className="relative rounded-lg border shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col"
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
            {isEdit ? `Edit ${team.name}` : 'Add team'}
          </h2>
          <button onClick={onClose} className="opacity-40 hover:opacity-100 transition-opacity" style={{ color: 'var(--text-2)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {serverErr && (
            <div className="rounded p-3 text-xs" style={{ background: 'var(--red-dim)', color: 'var(--red)' }}>
              {serverErr}
            </div>
          )}

          {/* Product & Value stream */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product">
              <select
                value={form.productId}
                onChange={(e) => set('productId')(e.target.value)}
                className={`${inputStyle.base} appearance-none`}
                style={inputCls()}
              >
                <option value="">— None —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Value stream" hint="Optional">
              {renderInput('valueStream', { placeholder: 'e.g. Billing' })}
            </Field>
          </div>

          <Field label="Team name" required error={errors.name}>
            {renderInput('name', { placeholder: 'Team name' })}
          </Field>

          {/* Jira */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Jira</span>
              <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Board ID" required hint="Numeric ID from Jira board URL" error={errors.jiraBoardId}>
                {renderInput('jiraBoardId', { mono: true, placeholder: '42' })}
              </Field>
              <Field label="Project key(s)" required hint="Comma-separated if multiple" error={errors.jiraProjectKey}>
                <input
                  type="text"
                  value={form.jiraProjectKey}
                  onChange={(e) => set('jiraProjectKey')(e.target.value.toUpperCase())}
                  placeholder="e.g. THOR"
                  className={`${inputStyle.base} font-mono`}
                  style={inputCls(errors.jiraProjectKey)}
                />
              </Field>
            </div>
            <Field label="Sprint name filter" hint='Only sync sprints containing this text (e.g. "Apollo"). Leave blank to sync all.'>
              {renderInput('sprintNameFilter', { placeholder: 'Apollo' })}
            </Field>
          </div>

          {/* Bitbucket — hidden for now
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Bitbucket</span>
              <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Workspace" required error={errors.bitbucketWorkspace}>
                {renderInput('bitbucketWorkspace', { mono: true, placeholder: 'your-org' })}
              </Field>
              <Field label="Repo slug" required error={errors.bitbucketRepoSlug}>
                {renderInput('bitbucketRepoSlug', { mono: true, placeholder: 'thor' })}
              </Field>
            </div>
            <Field label="PR labels" required hint='Comma-separated · e.g. "webui-full, webui-regression"' error={errors.bitbucketPrLabels}>
              {renderInput('bitbucketPrLabels', { placeholder: 'webui-full, webui-regression' })}
            </Field>
          </div>
          */}

          {/* Settings */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Settings</span>
              <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            </div>
            {/* Timezone — hidden for now, defaults to Europe/London
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Timezone">
                <select
                  value={form.timezone}
                  onChange={(e) => set('timezone')(e.target.value)}
                  className={`${inputStyle.base} appearance-none`}
                  style={inputCls()}
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </Field>
            </div>
            */}
            <div className="mb-3">
              <Field label="Added by">
                <input
                  type="text"
                  value={form.createdBy}
                  readOnly
                  className={`${inputStyle.base} cursor-default opacity-70`}
                  style={inputCls()}
                />
              </Field>
            </div>

            {/* Active toggle */}
            <div
              className="flex items-center justify-between rounded border p-3"
              style={{ background: 'var(--bg-subtle)', borderColor: 'var(--border)' }}
            >
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-1)' }}>Activate data collection</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                  When active, metrics are collected nightly from Jira
                </p>
              </div>
              <button
                type="button"
                onClick={() => set('isActive')(!form.isActive)}
                className="w-10 h-5 rounded-full transition-colors relative shrink-0"
                style={{ background: form.isActive ? 'var(--green)' : 'var(--border-mid)' }}
              >
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                  style={{ transform: form.isActive ? 'translateX(21px)' : 'translateX(2px)' }}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4 border-t"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs rounded border transition-opacity hover:opacity-70"
            style={{ borderColor: 'var(--border)', color: 'var(--text-2)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={pending}
            className="flex items-center gap-2 px-4 py-2 text-xs rounded font-medium transition-opacity disabled:opacity-40 hover:opacity-80"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            {pending && <Loader2 size={12} className="animate-spin" />}
            {isEdit ? 'Save changes' : 'Add team'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}